import React, { useState, useEffect, useRef } from "react";
import { Send, X } from "lucide-react";
import { createSocket } from "../../config/socket.config";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import toast from "react-hot-toast";

const Chat = ({
  requestId,
  recipientId,
  senderNonprofitId,
  recipientNonprofitId,
  currentUserNonprofitId,
  isVisible,
  onClose,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll to bottom when new messages arrive (within messages container only)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const fetchMessages = async (cursor = null) => {
    if (!threadId) {
      setMessages([]);
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    try {
      if (cursor) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const url = cursor
        ? `${BASE_URL}${API_ENDPOINTS.GET_CHAT_MESSAGES}/${threadId}/messages?cursor=${cursor}&limit=50`
        : `${BASE_URL}${API_ENDPOINTS.GET_CHAT_MESSAGES}/${threadId}/messages?limit=50`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to fetch messages");
      }

      const data = result?.data || result;
      const fetchedMessages = data.messages || [];

      if (cursor) {
        // Prepend older messages
        setMessages((prev) => [...fetchedMessages, ...prev]);
      } else {
        // For initial load, only set if we don't have messages already
        // This prevents clearing messages that were just sent
        setMessages((prev) => {
          if (prev.length > 0) {
            const fetchedIds = new Set(fetchedMessages.map((m) => m.id));
            const extraFromRealtime = prev.filter(
              (m) => !fetchedIds.has(m.id),
            );
            return [...fetchedMessages, ...extraFromRealtime];
          }
          return fetchedMessages;
        });
        // Scroll to bottom after initial load
        setTimeout(scrollToBottom, 100);
      }

      setHasMore(data.hasMore || false);
      setNextCursor(data.nextCursor || null);
    } catch (error) {
      console.error("Fetch messages error:", error);
      if (!cursor) {
        toast.error(error?.message || "Failed to load messages");
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Initialize socket connection
  useEffect(() => {
    if (!isVisible) return;

    // Don't connect if we don't have required data yet
    if (!requestId || !senderNonprofitId || !recipientNonprofitId) {
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    // Create socket connection
    const socket = createSocket(token);
    socketRef.current = socket;

    // Connection events
    socket.on("connect", () => {
      setIsConnected(true);
      setIsAuthenticated(false); // Reset auth state on new connection
    });

    // Listen for authentication confirmation from server
    socket.on("authenticated", (data) => {
      setIsAuthenticated(true);

      // Join thread AFTER authentication is confirmed
      if (threadId) {
        socket.emit("join_thread", { threadId });
      }
    });

    socket.on("disconnect", (reason) => {
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    // Listen for connection_error from server
    socket.on("connection_error", (error) => {
      console.error("Server connection error:", error);
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    // Listen for new messages
    socket.on("new_message", (message) => {
      setMessages((prev) => {
        // Check if message already exists (avoid duplicates)
        const exists = prev.some((m) => m.id === message.id);
        if (exists) return prev;
        return [...prev, message];
      });
      setTimeout(scrollToBottom, 100);
    });

    // Listen for message sent confirmation
    socket.on("message_sent", (data) => {
      if (data.threadId && !threadId) {
        // Set threadId but DON'T fetch messages yet
        // The new_message event already added it to the UI
        setThreadId(data.threadId);
      }
    });

    // Listen for errors
    socket.on("error", (error) => {
      console.error("Socket error:", error);
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, requestId, senderNonprofitId, recipientNonprofitId]); // Removed threadId - it shouldn't cause reconnect

  // Check if thread exists when component becomes visible
  useEffect(() => {
    if (!isVisible || !requestId || !senderNonprofitId || !recipientNonprofitId)
      return;

    const fetchThreadId = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      try {
        const url = `${BASE_URL}${API_ENDPOINTS.GET_CHAT_THREAD_ID}?collaborationRequestId=${requestId}&senderNonprofitId=${senderNonprofitId}&recipientNonprofitId=${recipientNonprofitId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json().catch(() => null);
        const statusCode = result?.statusCode ?? response.status;

        if (response.ok && statusCode < 400) {
          const data = result?.data || result;
          if (data.threadId) {
            setThreadId(data.threadId);
          } else {
            setThreadId(null);
            setMessages([]);
          }
        } else {
          // Thread doesn't exist yet, that's okay
          setThreadId(null);
          setMessages([]);
        }
      } catch (error) {
        console.error("Fetch thread ID error:", error);
        setThreadId(null);
        setMessages([]);
      }
    };

    fetchThreadId();
  }, [isVisible, requestId, senderNonprofitId, recipientNonprofitId]);

  // Load messages when threadId is available
  useEffect(() => {
    if (threadId && isVisible) {
      fetchMessages();
      // Join thread room if socket is already connected AND authenticated
      if (socketRef.current?.connected && isAuthenticated) {
        socketRef.current.emit("join_thread", { threadId });
      }
      // If not authenticated yet, the "authenticated" event handler will join the thread
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, isVisible, isAuthenticated]);

  const handleSend = () => {
    if (input.trim() === "") return;

    if (
      !socketRef.current ||
      !socketRef.current.connected ||
      !isConnected ||
      !isAuthenticated
    ) {
      toast.error("Not connected. Please wait...");
      return;
    }

    // Additional check: verify we have all required data
    if (!requestId || !senderNonprofitId || !recipientNonprofitId) {
      toast.error("Missing request data. Please refresh the page.");
      return;
    }

    const messageContent = input.trim();
    setInput("");

    // Emit send_message event
    // Note: senderNonprofitId and recipientNonprofitId are the original sender/recipient from the request
    // The backend will use the current user (from socket auth) as the actual message sender
    socketRef.current.emit("send_message", {
      collaborationRequestId: requestId,
      senderNonprofitId: senderNonprofitId, // Original sender from request
      recipientNonprofitId: recipientNonprofitId, // Partner NGO
      threadId: threadId || null,
      content: messageContent,
    });
  };

  const handleLoadOlderMessages = () => {
    if (nextCursor && !isLoadingMore) {
      fetchMessages(nextCursor);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      // Format as MM/DD/YYYY
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Determine if message is from current user
  const isCurrentUserMessage = (message) => {
    return message.senderNonprofitId === currentUserNonprofitId;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg h-[85vh] flex flex-col border border-gray-100 shadow">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-gray-200">
        <div className="text-center flex-1">
          <h2 className="text-lg font-popinsSemiBold text-darkColor">Chat</h2>
          <p
            className={`font-popinsRegular text-xs ${
              isAuthenticated ? "text-green-600" : "text-red-600"
            }`}
          >
            {isAuthenticated ? "Connected" : "Disconnected"}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close chat"
          >
            <X size={20} className="text-gray-500" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-6 panelScroll"
      >
        {isLoading && messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-400 font-popinsRegular">
              Loading messages...
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-full text-center">
            <p className="text-gray-400 font-popinsRegular text-sm">
              Start a chat
            </p>
            <p className="text-gray-300 font-popinsRegular text-xs mt-2">
              Send a message to begin the conversation
            </p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center">
                <button
                  onClick={handleLoadOlderMessages}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-sm text-themeColor font-popinsMedium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? "Loading..." : "Show older messages"}
                </button>
              </div>
            )}
            {messages.map((msg) => {
              const isCurrentUser = isCurrentUserMessage(msg);
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col ${
                    isCurrentUser ? "items-end" : "items-start"
                  }`}
                >
                  {/* Sender Name */}
                  <p className="text-sm font-popinsBold text-darkColor mb-1">
                    {msg.senderOrganizationName || "Unknown"}
                  </p>

                  {/* Message Bubble */}
                  <div
                    className={`max-w-xs font-popinsMedium p-3 text-sm shadow-sm ${
                      isCurrentUser
                        ? "bg-gray-100 text-darkColor rounded-2xl rounded-br-none"
                        : "bg-themeColor/30 text-darkColor rounded-2xl rounded-bl-none"
                    }`}
                  >
                    {msg.content}
                  </div>

                  {/* Time */}
                  <span className="text-xs font-popinsRegular text-darkColor/40 mt-1">
                    {formatDate(msg.createdAt)}
                  </span>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="bg-white w-full p-4 flex items-center gap-2 sticky bottom-0 border-t border-gray-200">
        <input
          type="text"
          placeholder="Type here"
          className="peer w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSend()}
          disabled={!isConnected}
        />

        <button
          onClick={handleSend}
          disabled={
            !isAuthenticated ||
            input.trim() === "" ||
            !requestId ||
            !senderNonprofitId ||
            !recipientNonprofitId
          }
          className="bg-[#0381CA] text-white p-2 rounded-lg hover:bg-[#0381CA]/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            !isAuthenticated
              ? "Authenticating..."
              : !requestId
                ? "Loading request data..."
                : ""
          }
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default Chat;
